/**
 * メールのテンプレートからも読むので、ここは何も import しない (メールのプレビューが依存を辿って
 * Worker 側のモジュールまで読みに行き、起動できなくなる)。
 *
 * キャンセルしたときに、お金がどう戻るか (= 顧客と CS にどう伝えるか)。
 *
 *  - "no_payment":           お支払い前 (Shopify が受け取った額が 0 円)。ご請求もご返金も無い
 *  - "auto_refund":          カード / PayPay / ペイディ / 楽天ペイ等。Shopify から決済会社経由で返金される
 *  - "cash_refund":          コンビニ決済 / 銀行振込で入金済み。Shopify を経由していないので、口座を伺って
 *                            銀行振込で手動返金する
 *  - "np_invoice_cancelled": NP 後払い。Shopify 上は注文時に支払い済み扱いだが、実際は請求の取り消しで
 *                            済む (お客様は届いた請求書を破棄するだけ)
 *  - "manual_refund":        入金はあるが、Shopify がキャンセルを受け付けず「閉じる」(アーカイブ) だけに
 *                            なった。返金 (NP 後払いなら請求の取り消し) は担当者が別途行う
 *                            (`resolveCancelRefundKind`)
 *
 * 判定は `classifyCancelRefund` (payment.ts)。**受け取った額** (`totalReceivedSet`) と支払い方法で行う。支払いの状態
 * (`displayFinancialStatus`) はキャンセルの後に REFUNDED / VOIDED 等へ変わるので、キャンセル前に
 * 送るメールと、キャンセル後に見る注文詳細とで答えが割れてしまう。受け取った額はキャンセルで変わらない。
 */
export type CancelRefundKind =
  | "no_payment"
  | "auto_refund"
  | "cash_refund"
  | "np_invoice_cancelled"
  | "manual_refund";

/**
 * キャンセルしたときの返金の伝え方 (顧客向け)。完了メール・注文詳細・完了画面がすべてここを読む
 * (場所ごとに書くと食い違う)。区分は `classifyCancelRefund`。
 *
 * - `status`: 状態を 1 行で言うもの (メールの「ステータス」・注文詳細の見出し)
 * - `body`: 何をすればよいか / 何が起きるかの説明
 *
 * 銀行振込で返金する場合の口座のお願い (項目の一覧・ボタン) は、出す場所ごとの形があるので各所に持つ。
 */
export const CANCEL_REFUND_TEXT: Record<CancelRefundKind, { status: string; body: string }> = {
  no_payment: {
    status: "キャンセル完了 (お支払い前のため、ご請求・ご返金はありません)",
    body: "お支払い前のキャンセルのため、ご請求・ご返金はございません。",
  },
  auto_refund: {
    status: "キャンセル完了 (ご返金のお手続きをいたしました)",
    body: "ご返金のお手続きをいたしました。返金の時期や方法は決済方法により異なります。返金状況は弊社ではお調べできませんので、クレジットカード会社または決済サービス会社にお問い合わせください。",
  },
  cash_refund: {
    status: "キャンセル完了 (ご返金用の口座をお知らせください)",
    body: "お支払い方法がコンビニ決済もしくは銀行振込のため、銀行振込にてご返金いたします。",
  },
  manual_refund: {
    status: "キャンセル完了 (ご返金は担当者よりあらためてご案内いたします)",
    body: "ご返金のお手続きは、担当者よりあらためてご案内いたします。",
  },
  np_invoice_cancelled: {
    status: "キャンセル完了 (NP後払いのご請求を取り消しました)",
    body: "NP後払いのご請求は取り消しております。お手元に請求書が届いている場合は、お手数ですが破棄してください。",
  },
};

/**
 * キャンセルする**前**に見せる返金の案内 (キャンセルの案内ページ)。お支払い方法ごとに全部並べる
 * (注文ごとに出し分けない)。キャンセル後の {@link CANCEL_REFUND_TEXT} と食い違わないよう、ここで
 * 並べて持つ。
 */
export const CANCEL_REFUND_GUIDE: { method: string; body: string }[] = [
  {
    method: "クレジットカード・PayPay・ペイディ・楽天ペイなど",
    body: "キャンセルのお手続き後、ご利用の決済方法を通じてご返金いたします。返金の時期や方法は決済方法により異なります。返金状況は弊社ではお調べできませんので、クレジットカード会社または決済サービス会社にお問い合わせください。",
  },
  {
    method: "コンビニ決済・銀行振込 (お支払い済みの場合)",
    body: "キャンセルのお手続き後、銀行振込にてご返金いたします。返金先の口座は、キャンセル完了のメールでお伺いします。",
  },
  {
    method: "NP後払い",
    body: "ご請求を取り消します。お手元に請求書が届いている場合は、キャンセル完了のメールをご確認のうえ破棄してください。",
  },
  {
    method: "お支払い前のご注文",
    body: "キャンセル後のご請求・ご返金はありません。",
  },
];
